import { createRef, useState, useRef, useEffect, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { get, map, chain, isEmpty, find, noop, size, first as _first } from "lodash"
import { Button, Popover, MenuList, MenuItem, Typography, TextField, InputAdornment, Box, IconButton, Menu } from "@mui/material"
import Tooltip from "components/material/tooltip"
import { Search, ArrowDropDown, ArrowDropUp, Info, MoreVert } from "@mui/icons-material"
import NewFeature from "modules/new-feature"
import { SEARCHABLE_MENU_ITEM } from "helpers/shapes"
import { getHighlightedText } from "helpers/menu"
import { createMatch } from "helpers/string"

import "./style.scss"

const DEFAULT = {
  open: false,
  search: "",
  selected: null,
}

export default function SearchableMenu({
  items,
  current,
  onSelect,
  className,
  placeholder = "Search views",
  noResultsText = "No views match your search",
  noSelectionText = "Change view",
  hideSearchBox,
  getLabel,
  disabled = false,
  disablePortal = false,
}) {
  const [open, setOpen] = useState(DEFAULT.open)
  const [search, setSearch] = useState(DEFAULT.search)
  const [selected, setSelected] = useState(DEFAULT.selected)
  const [menuState, setMenuState] = useState({ anchorEl: null, item: null })

  const firstItem = useRef(createRef())
  const prevOpen = useRef(open)
  const anchorRef = useRef(null)

  const newFeature = useMemo(() => get(find(items, (i) => !!i.newFeature), "newFeature"), [items])

  const match = useMemo(() => createMatch(search), [search])

  const orderedItems = useMemo(
    () =>
      chain(items)
        .filter(({ label }) => isEmpty(search) || match(label))
        .orderBy(["order", "asc"])
        .value(),
    [items, search, match],
  )

  // set selected item
  useEffect(() => {
    const item = find(items, ({ id }) => id === current)
    setSelected(item)
  }, [items, current])

  // return focus to the button when we transitioned from !open -> open
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus()
    }

    prevOpen.current = open
  }, [open])

  const close = useCallback(() => {
    setOpen(DEFAULT.open)
    setSearch(DEFAULT.search)
  }, [])

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev)
  }, [])

  const handleKeyDown = useCallback(
    (e) => {
      const { key } = e
      if (key === "Enter" && size(orderedItems) === 1) {
        const item = _first(orderedItems)
        setSelected(item)
        onSelect(item)
        close()
      } else if (key === "ArrowDown" && firstItem.current) {
        firstItem.current.focus()
      }
    },
    [orderedItems, onSelect, close],
  )

  const onChange = useCallback((e) => {
    setSearch(e.target.value)
  }, [])

  const makeClick = useCallback(
    (item) => () => {
      setSelected(item)
      onSelect(item)
      close()
    },
    [onSelect, close],
  )

  const handleActionClick = useCallback((action) => {
    action.onClick()
    setMenuState({ anchorEl: null, item: null })
    setOpen(false)
  }, [])

  const handleMenuClose = useCallback(() => {
    setMenuState({ anchorEl: null, item: null })
  }, [])

  const handlePrimaryActionOpen = useCallback((e, item) => {
    setMenuState({ anchorEl: e.currentTarget, item })
  }, [])

  const selectedButton = useMemo(() => {
    let label = selected ? selected.label : noSelectionText
    if (newFeature) {
      label = <NewFeature id={[newFeature, "btn"].join("-")}>{label}</NewFeature>
    }
    return (
      <Button
        ref={anchorRef}
        aria-controls={open ? "menu-list-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        endIcon={open ? <ArrowDropUp /> : <ArrowDropDown />}
        disabled={disabled}
      >
        <span data-test-class="searchable-menu-label">
          {getLabel ? getLabel(label) : label}
        </span>
      </Button>
    )
  }, [newFeature, anchorRef, open, selected, noSelectionText, getLabel, disabled, handleToggle])

  return <div className={classNames("components_searchable-menu", className)}>
    {selectedButton}
    <Popover
      open={open}
      anchorEl={anchorRef.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      classes={{ root: className }}
      className="searchable-menu-popover"
      disablePortal={disablePortal}
      onClose={close}
    >
      <Box>
        {!hideSearchBox && <>
          <TextField
            variant="outlined"
            autoFocus
            size="small"
            placeholder={placeholder}
            value={search}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
          />
          {isEmpty(orderedItems) && <Typography tabIndex={-1} variant="subtitle2" className="no-items">{noResultsText}</Typography>}
        </>}
        <MenuList className="menu-list" id="menu-list-grow">
          {map(orderedItems, (item, i) => {
            const fragments = []
            // On group change
            if (i === 0 || (i > 0 && item.groupId !== orderedItems[i - 1].groupId)) {
              fragments.push(<Typography key={`${item.groupId}-${i}`} tabIndex={-1} variant="subtitle1">{item.groupLabel}</Typography>)
            }

            let link = (
              <>
                {getHighlightedText(item.label, search)}
                {item.tooltip && <Tooltip title={item.tooltip}><Info fontSize="small" /></Tooltip>}
              </>
            )
            if (item.newFeature) {
              link = <NewFeature id={item.newFeature}>{link}</NewFeature>
            }

            const isSelected = selected && item.id === selected.id
            const hasSecondaryAction = item.secondaryAction?.icon && item.secondaryAction?.onClick
            const handleSecondaryClick = (e) => {
              e.stopPropagation()
              item.secondaryAction.onClick()
              close()
            }
            const fragment = <MenuItem
              key={item.id}
              ref={i === 0 ? firstItem : null}
              disabled={item.disabled}
              selected={isSelected}
              tabIndex={0}
              onClick={!item.disabled ? makeClick(item) : noop}
            >
              {link}
            </MenuItem>

            const SecondaryIcon = item.secondaryAction?.icon
            const secondaryActionButton = hasSecondaryAction && SecondaryIcon ? (
              <IconButton
                key="secondary"
                className={classNames("square-button", "searchable-menu-secondary-action-button", { selected: isSelected })}
                size="small"
                onClick={handleSecondaryClick}
                aria-label="Open in new tab"
              >
                <SecondaryIcon fontSize="small" />
              </IconButton>
            ) : null

            const primaryActionButton = !isEmpty(item.actions) ? (
              <IconButton
                key="actions"
                className={classNames("square-button", { selected: isSelected })}
                size="small"
                onClick={(e) => handlePrimaryActionOpen(e, item)}
              >
                <MoreVert />
              </IconButton>
            ) : null

            const hasAnyActions = secondaryActionButton || primaryActionButton

            fragments.push(hasAnyActions
              ? <span className={classNames("has-actions", { "is-selected": isSelected })} key={item.id}>
                {fragment}
                {primaryActionButton && (
                  <span className="searchable-menu-primary-actions">
                    {primaryActionButton}
                  </span>
                )}
                {secondaryActionButton && (
                  <span className="searchable-menu-action-buttons searchable-menu-secondary-action">
                    {secondaryActionButton}
                  </span>
                )}
              </span>
              : fragment,
            )

            return fragments
          })}
        </MenuList>
        <Menu
          id="contextual-searchable-menu-popover"
          anchorEl={menuState.anchorEl}
          keepMounted
          open={Boolean(menuState.anchorEl)}
          onClose={handleMenuClose}
          disablePortal={disablePortal}
        >
          {map(menuState.item?.actions, (action) => <MenuItem key={action.id} onClick={() => handleActionClick(action)}>{action.label || action.component}</MenuItem>)}
        </Menu>
      </Box>
    </Popover>
  </div>
}

SearchableMenu.propTypes = {
  current: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  className: PropTypes.string,
  items: PropTypes.arrayOf(SEARCHABLE_MENU_ITEM),
  placeholder: PropTypes.string,
  noSelectionText: PropTypes.string,
  noResultsText: PropTypes.string,
  hideSearchBox: PropTypes.bool,
  getLabel: PropTypes.func,
  disabled: PropTypes.bool,
  disablePortal: PropTypes.bool,
}
